'use client';

import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';

import {
	buildFieldPath as buildPath, isNonEmptyString, resetField,
} from '@/modules/shared/presentation/utils';
import type { BuildFieldPath } from '@/modules/shared/presentation/utils';

import type { SelectInputProps } from '@packages/hook-form-wrapper/src/components/inputs';

type FieldKey = 'categoryId' | 'specializationId' | 'subCategoryId';

type OccupationFormFields = {
	categoryId?:       string
	specializationId?: string
	subCategoryId?:    string
};

type OccupationFieldValues = FieldValues & (
	| OccupationFormFields
	| Record<string, OccupationFormFields>
);

function buildFieldPath<
	TFieldValues extends OccupationFieldValues,
	TBaseName extends string | undefined,
	TFieldKey extends FieldKey,
>(
	baseName: TBaseName | undefined,
	fieldKey: TFieldKey,
): BuildFieldPath<TFieldValues, TBaseName, TFieldKey> {
	return buildPath(baseName, fieldKey) as BuildFieldPath<TFieldValues, TBaseName, TFieldKey>;
}

export function useOccupationFormWatch<
	TFieldValues extends OccupationFieldValues,
	TBaseName extends string | undefined = undefined,
>(baseName?: TBaseName) {
	const { control, setValue } = useFormContext<TFieldValues>();

	const categoryPath = buildFieldPath<TFieldValues, TBaseName, 'categoryId'>(baseName, 'categoryId');
	const subCategoryPath = buildFieldPath<TFieldValues, TBaseName, 'subCategoryId'>(baseName, 'subCategoryId');
	const specializationPath = buildFieldPath<TFieldValues, TBaseName, 'specializationId'>(baseName, 'specializationId');

	const categoryIdValue = useWatch({ control, name: categoryPath });
	const subCategoryIdValue = useWatch({ control, name: subCategoryPath });

	const categoryId = isNonEmptyString(categoryIdValue) ? categoryIdValue : undefined;
	const subCategoryId = isNonEmptyString(subCategoryIdValue) ? subCategoryIdValue : undefined;

	const category: Pick<SelectInputProps<TFieldValues>, 'name'> = { name: categoryPath };
	const subCategory: Pick<SelectInputProps<TFieldValues>, 'disabled' | 'name'> = {
		disabled: !categoryId,
		name:     subCategoryPath,
	};
	const specialization: Pick<SelectInputProps<TFieldValues>, 'disabled' | 'name'> = {
		disabled: !subCategoryId,
		name:     specializationPath,
	};

	const prevCategoryId = useRef<string | undefined>(categoryId);
	const prevSubCategoryId = useRef<string | undefined>(subCategoryId);

	useEffect(() => {
		if (prevCategoryId.current !== undefined && categoryId !== prevCategoryId.current) {
			resetField(setValue, subCategoryPath);
			resetField(setValue, specializationPath);
		}

		prevCategoryId.current = categoryId;
	}, [ categoryId, setValue, subCategoryPath, specializationPath ]);

	useEffect(() => {
		if (prevSubCategoryId.current !== undefined && subCategoryId !== prevSubCategoryId.current) {
			resetField(setValue, specializationPath);
		}

		prevSubCategoryId.current = subCategoryId;
	}, [ subCategoryId, setValue, specializationPath ]);

	return {
		categoryId,
		subCategoryId,
		category,
		specialization,
		subCategory,
	};
}
